import React, { lazy, Suspense, useState, useEffect, useMemo } from "react";
import {
    Box,
    Container,
    Divider,
    Typography,
    Stack,
} from "@mui/material";

import { useTranslate } from "../components/Translate/TranslateContext";
import OptionsMenu from "../components/Options/OptionsMenu";
import ManualMenu from "../components/Manual/ManualMenu";

import DashboardTopSelector from "../components/Layout/DashboardTopSelector";
import DashboardFieldGroupSelector from "../components/Layout/DashboardFieldGroupSelector";
import { fetchBlogFeed } from "../api/blogFeed";
import { getGardenData } from "../api/gardenData";
import Banner from "./Banner";

const WeatherForecastPage = lazy(() => import("./WeatherForecastPage"));
const FarmData = lazy(() => import("./FarmDataPage"));
const FarmManagementPage = lazy(() => import("./FarmManagementPage"));
const GardenProfilePage = lazy(() => import("./GardenProfilePage"));
const GardenSoilPage = lazy(() => import("./GardenSoilPage"));
const OverviewPage = lazy(() => import("./OverviewPage"));
const AlertPage = lazy(() => import("./AlertPage"));
const BenchmarkPage = lazy(() => import("./BenchmarkPage"));
const ActivityLogPage = lazy(() => import("./ActivityLogPage"));

const PAGES = {
    OVERVIEW: "overview",
    ALERTS: "alerts",
    BENCHMARKS: "benchmarks",
    FORECAST: "weatherForecast",
    FARM: "farmData",
    FARMMANAGEMENT: "farm_management",
    CROPPERFORMANCE: "garden_profile",
    SOIL: "garden_soil",
    ACTIVITY: "activity_log",
};

function parseGardenIds(value) {
    if (Array.isArray(value)) {
        return value
            .map((gardenId) => Number(gardenId))
            .filter((gardenId) => Number.isFinite(gardenId));
    }

    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);

            if (Array.isArray(parsed)) {
                return parsed
                    .map((gardenId) => Number(gardenId))
                    .filter((gardenId) => Number.isFinite(gardenId));
            }
        } catch {
            return [];
        }
    }

    return [];
}

function extractGardens(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.gardens)) return data.gardens;
    return [];
}

function extractGardenGroups(data) {
    if (Array.isArray(data?.garden_groups)) return data.garden_groups;
    if (Array.isArray(data?.groups?.all_groups)) return data.groups.all_groups;

    const userGroups = Array.isArray(data?.groups?.user_groups)
        ? data.groups.user_groups
        : [];

    const farmCanonGroups = Array.isArray(data?.groups?.farm_canon_groups)
        ? data.groups.farm_canon_groups
        : [];

    return [...farmCanonGroups, ...userGroups];
}

function normalizeGarden(garden) {
    return {
        garden_id: Number(garden.garden_id),
        garden_name: garden.garden_name,
        farm_id: garden.farm_id,
        garden_type: garden.garden_type ?? null,
        cultivar_type: garden.cultivar_type ?? null,
        coordinates: {
            lat: Number(garden.coordinates?.lat ?? 0),
            lon: Number(garden.coordinates?.lon ?? 0),
        },
        geometry_type: garden.geometry_type ?? null,
        geometry: garden.geometry ?? null,
        forecast: Array.isArray(garden.forecast) ? garden.forecast : [],
    };
}

function normalizeGardenGroup(group, index) {
    const rawGroupId =
        group.group_id ??
        group.raw_group_id ??
        group.farm_id ??
        index;

    return {
        group_id: String(rawGroupId),
        raw_group_id: group.raw_group_id ?? null,
        group_name: group.group_name ?? group.farm_name ?? `Group ${index + 1}`,
        farm_canon: Boolean(group.farm_canon ?? group.is_canon ?? false),
        farm_id:
            group.farm_id === null || group.farm_id === undefined
                ? null
                : Number(group.farm_id),
        garden_ids: parseGardenIds(group.garden_ids),
    };
}

export default function UnifiedDashboard({ user, onLogout, setUser }) {
    const { translate } = useTranslate();
    const user_id = user?.user_id;

    const [page, setPage] = useState(PAGES.OVERVIEW);
    const [summaries, setSummaries] = useState([]);
    const [gardenGroups, setGardenGroups] = useState([]);
    const [selectedScope, setSelectedScope] = useState({
        type: "garden",
        id: null,
    });
    const [blogs, setBlogs] = useState([]);
    const [blogIndex, setBlogIndex] = useState(0);

    const supportsGroupSelection = useMemo(() => {
        return page !== PAGES.CROPPERFORMANCE;
    }, [page]);

    useEffect(() => {
        if (!user_id) return;

        let alive = true;

        (async () => {
            try {
                const data = await getGardenData(user_id);
                if (!alive) return;

                setSummaries(extractGardens(data));
                setGardenGroups(extractGardenGroups(data));
            } catch {
                if (!alive) return;

                setSummaries([]);
                setGardenGroups([]);
                setSelectedScope({
                    type: "garden",
                    id: null,
                });
            }
        })();

        return () => {
            alive = false;
        };
    }, [user_id]);

    const gardens = useMemo(() => {
        if (!Array.isArray(summaries) || summaries.length === 0) return [];

        return summaries
            .map(normalizeGarden)
            .filter((garden) => Number.isFinite(garden.garden_id));
    }, [summaries]);

    const normalizedGardenGroups = useMemo(() => {
        if (!Array.isArray(gardenGroups) || gardenGroups.length === 0) return [];

        return gardenGroups
            .map(normalizeGardenGroup)
            .filter((group) => group.garden_ids.length > 0)
            .sort((a, b) => {
                if (a.farm_canon !== b.farm_canon) {
                    return a.farm_canon ? -1 : 1;
                }

                return a.group_name.localeCompare(b.group_name);
            });
    }, [gardenGroups]);

    useEffect(() => {
        if (gardens.length === 0) {
            setSelectedScope({
                type: "garden",
                id: null,
            });
            return;
        }

        setSelectedScope((prev) => {
            const accessibleGardenIds = new Set(
                gardens.map((garden) => garden.garden_id)
            );

            const validGarden =
                prev?.type === "garden" &&
                prev.id !== null &&
                accessibleGardenIds.has(Number(prev.id));

            const selectedGroup =
                prev?.type === "group"
                    ? normalizedGardenGroups.find(
                          (group) => group.group_id === String(prev.id)
                      )
                    : null;

            if (!supportsGroupSelection) {
                if (validGarden) return prev;

                const firstGardenFromGroup = selectedGroup?.garden_ids?.find(
                    (gardenId) => accessibleGardenIds.has(Number(gardenId))
                );

                return {
                    type: "garden",
                    id: firstGardenFromGroup ?? gardens[0]?.garden_id ?? null,
                };
            }

            const validGroup =
                prev?.type === "group" &&
                normalizedGardenGroups.some(
                    (group) => group.group_id === String(prev.id)
                );

            if (validGroup || validGarden) return prev;

            if (normalizedGardenGroups.length > 0) {
                return {
                    type: "group",
                    id: normalizedGardenGroups[0].group_id,
                };
            }

            return {
                type: "garden",
                id: gardens[0]?.garden_id ?? null,
            };
        });
    }, [gardens, normalizedGardenGroups, supportsGroupSelection]);

    const selectedGardenGroup = useMemo(() => {
        if (!supportsGroupSelection) return null;
        if (selectedScope.type !== "group") return null;

        return (
            normalizedGardenGroups.find(
                (group) => group.group_id === String(selectedScope.id)
            ) || null
        );
    }, [normalizedGardenGroups, selectedScope, supportsGroupSelection]);

    const selectedGardenIds = useMemo(() => {
        const accessibleGardenIds = new Set(
            gardens.map((garden) => garden.garden_id)
        );

        if (selectedGardenGroup) {
            return selectedGardenGroup.garden_ids.filter((gardenId) =>
                accessibleGardenIds.has(Number(gardenId))
            );
        }

        if (selectedScope.type === "garden" && selectedScope.id !== null) {
            const gardenId = Number(selectedScope.id);
            return accessibleGardenIds.has(gardenId) ? [gardenId] : [];
        }

        return [];
    }, [gardens, selectedGardenGroup, selectedScope]);

    const selectedGardenId = useMemo(() => {
        if (selectedScope.type === "garden" && selectedScope.id !== null) {
            return Number(selectedScope.id);
        }

        return selectedGardenIds[0] ?? null;
    }, [selectedScope, selectedGardenIds]);

    const selectedGarden = useMemo(() => {
        if (!selectedGardenId) return null;

        return (
            gardens.find((garden) => garden.garden_id === selectedGardenId) ||
            null
        );
    }, [gardens, selectedGardenId]);

    const selectorValue = useMemo(() => {
        if (
            supportsGroupSelection &&
            selectedScope.type === "group" &&
            selectedScope.id !== null
        ) {
            return `group:${selectedScope.id}`;
        }

        if (selectedGardenId) {
            return `garden:${selectedGardenId}`;
        }

        return "";
    }, [selectedScope, selectedGardenId, supportsGroupSelection]);

    const showGardenSelector = useMemo(() => {
        return (
            page === PAGES.FARM ||
            page === PAGES.FARMMANAGEMENT ||
            page === PAGES.CROPPERFORMANCE ||
            page === PAGES.SOIL ||
            page === PAGES.ACTIVITY ||
            page === PAGES.ALERTS ||
            page === PAGES.BENCHMARKS
        );
    }, [page]);

    const handleScopeChange = (event) => {
        const value = String(event.target.value);

        if (value.startsWith("group:") && supportsGroupSelection) {
            setSelectedScope({
                type: "group",
                id: value.slice("group:".length),
            });
            return;
        }

        if (value.startsWith("garden:")) {
            setSelectedScope({
                type: "garden",
                id: Number(value.slice("garden:".length)),
            });
        }
    };

    const sharedSelectionProps = {
        summaries,
        gardens,
        garden_groups: normalizedGardenGroups,
        selectedGarden,
        selectedGardenGroup,
        selectedGardenIds,
        selectedScope,
    };

    useEffect(() => {
        let alive = true;

        (async () => {
            try {
                const xmlText = await fetchBlogFeed();
                if (!alive) return;

                const xml = new DOMParser().parseFromString(
                    xmlText,
                    "application/xml"
                );
                const items = [...xml.querySelectorAll("channel > item")].slice(
                    0,
                    3
                );

                setBlogs(
                    items
                        .map((item) => ({
                            title: item.querySelector("title")?.textContent?.trim(),
                            link: item.querySelector("link")?.textContent?.trim(),
                            img: item
                                .querySelector("enclosure")
                                ?.getAttribute("url"),
                        }))
                        .filter((blog) => blog.link)
                );
            } catch {
                if (!alive) return;
                setBlogs([]);
                setBlogIndex(0);
            }
        })();

        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        if (blogs.length < 2) return;

        const timer = setInterval(() => {
            setBlogIndex((index) => (index + 1) % blogs.length);
        }, 4000);

        return () => clearInterval(timer);
    }, [blogs]);

    return (
        <Box sx={{ background: "var(--color-bg-primary)", minHeight: "100vh" }}>
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backdropFilter: "saturate(180%) blur(6px)",
                    backgroundColor: "rgba(255,250,241,0.92)",
                    borderBottom: "1px solid var(--color-green-soft)",
                    py: 1.5,
                }}
            >
                <Container
                    maxWidth={false}
                    sx={{
                        px: 3,
                        py: 4,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 3,
                            mb: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            <a
                                href="https://tensoai.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img
                                    src="/tensoLogo.png"
                                    alt="TensoAI Logo"
                                    style={{
                                        width: "34px",
                                        height: "34px",
                                        objectFit: "contain",
                                        cursor: "pointer",
                                    }}
                                />
                            </a>

                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    color: "var(--color-text-primary)",
                                    lineHeight: 1,
                                }}
                            >
                                {translate("Farmsync")}
                            </Typography>
                        </Box>

                        <Stack direction="row" alignItems="center" spacing={1}>
                            <OptionsMenu
                                user={user}
                                onLogout={onLogout}
                                setUser={setUser}
                            />
                            <ManualMenu />
                        </Stack>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 2,
                        }}
                    >
                        <DashboardTopSelector
                            page={page}
                            setPage={setPage}
                            PAGES={PAGES}
                        />

                        {showGardenSelector && gardens.length > 0 && (
                            <DashboardFieldGroupSelector
                                value={selectorValue}
                                onChange={handleScopeChange}
                                gardens={gardens}
                                gardenGroups={normalizedGardenGroups}
                                supportsGroupSelection={supportsGroupSelection}
                            />
                        )}
                    </Box>
                </Container>
            </Box>

            <Container
                maxWidth={false}
                sx={{
                    px: 3,
                    py: 4,
                }}
            >
                {page === PAGES.OVERVIEW && gardens.length > 0 && (
                    <Suspense
                        fallback={
                            <Typography>{translate("loadingOverview")}</Typography>
                        }
                    >
                        <Banner
                            mode="next"
                            user_id={user_id}
                            summaries={summaries}
                            gardens={gardens}
                            garden_groups={normalizedGardenGroups}
                        />
                        <OverviewPage
                            summaries={summaries}
                            gardens={gardens}
                            garden_groups={normalizedGardenGroups}
                        />
                    </Suspense>
                )}

                {page === PAGES.FORECAST && gardens.length > 0 && (
                    <Suspense
                        fallback={
                            <Typography>{translate("loadingWeather")}</Typography>
                        }
                    >
                        <Banner
                            mode="next"
                            user_id={user_id}
                            summaries={summaries}
                            gardens={gardens}
                            garden_groups={normalizedGardenGroups}
                        />
                        <WeatherForecastPage
                            user_id={user_id}
                            {...sharedSelectionProps}
                        />
                    </Suspense>
                )}

                {page === PAGES.FARM && selectedGarden && (
                    <Suspense
                        fallback={
                            <Typography>{translate("loadingFarmData")}</Typography>
                        }
                    >
                        <FarmData
                            user_id={user_id}
                            {...sharedSelectionProps}
                        />
                    </Suspense>
                )}

                {page === PAGES.FARMMANAGEMENT && selectedGarden && (
                    <Suspense
                        fallback={
                            <Typography>
                                {translate("loadingFarmManagement")}
                            </Typography>
                        }
                    >
                        <FarmManagementPage
                            user_id={user_id}
                            {...sharedSelectionProps}
                        />
                    </Suspense>
                )}

                {page === PAGES.CROPPERFORMANCE && selectedGarden && (
                    <Suspense
                        fallback={
                            <Typography>
                                {translate("loadingCropPerformance")}
                            </Typography>
                        }
                    >
                        <GardenProfilePage
                            user_id={user_id}
                            selectedGarden={selectedGarden}
                            summaries={summaries}
                            gardens={gardens}
                            garden_groups={normalizedGardenGroups}
                            selectedGardenIds={[selectedGarden.garden_id]}
                            selectedGardenGroup={null}
                            selectedScope={{
                                type: "garden",
                                id: selectedGarden.garden_id,
                            }}
                        />
                    </Suspense>
                )}

                {page === PAGES.SOIL && selectedGardenIds.length > 0 && (
                    <Suspense
                        fallback={
                            <Typography>{translate("loadingSoil")}</Typography>
                        }
                    >
                        <GardenSoilPage
                            user_id={user_id}
                            {...sharedSelectionProps}
                        />
                    </Suspense>
                )}

                {page === PAGES.ACTIVITY && selectedGardenIds.length > 0 && (
                    <Suspense
                        fallback={
                            <Typography>
                                {translate("loadingActivityLog")}
                            </Typography>
                        }
                    >
                        <ActivityLogPage
                            user_id={user_id}
                            {...sharedSelectionProps}
                        />
                    </Suspense>
                )}

                {page === PAGES.ALERTS && selectedGarden && (
                    <Suspense
                        fallback={
                            <Typography>{translate("loadingAlerts")}</Typography>
                        }
                    >
                        <AlertPage
                            user_id={user_id}
                            {...sharedSelectionProps}
                        />
                    </Suspense>
                )}

                {page === PAGES.BENCHMARKS && selectedGarden && (
                    <Suspense
                        fallback={
                            <Typography>{translate("loadingAlerts")}</Typography>
                        }
                    >
                        <BenchmarkPage
                            user_id={user_id}
                            {...sharedSelectionProps}
                        />
                    </Suspense>
                )}
            </Container>

            <Divider sx={{ my: 3, borderColor: "var(--color-green-soft)" }} />

            <Container maxWidth="lg" sx={{ py: 6 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: { xs: "center", md: "flex-end" },
                        alignItems: "flex-end",
                        minHeight: 140,
                    }}
                >
                    {blogs.length > 0 && blogs[blogIndex] && (
                        <Box sx={{ textAlign: "center" }}>
                            <a
                                href={blogs[blogIndex].link}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <img
                                    src={blogs[blogIndex].img}
                                    alt={blogs[blogIndex].title}
                                    style={{
                                        width: "100%",
                                        maxWidth: 200,
                                        height: "auto",
                                        borderRadius: 6,
                                        objectFit: "contain",
                                    }}
                                />
                            </a>
                        </Box>
                    )}
                </Box>
            </Container>
        </Box>
    );
}
